/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Modal,
  Spinner,
  SvgIcon,
  RequestPropertiesBar,
  CreateCaseModal,
  RequestClassify,
  RequestAllocate,
  RequestCapture,
  RequestValidate,
  RequestWorkflow,
  RequestOrigin,
  CenteredContainerDisplay,
  FileView,
  FileViewer,
  Email,
  Button,
  Warning,
  Back
} from '../'

import {
  decodeBase64,
  getNodeTypeFromBase64Id
} from '../../utils'

import {
  QUERY_FILE,
  QUERY_REQUESTRUN,
  QUERY_CASERELATIONS
} from '../../queries'

import {
  CONTENTTYPE_CASE,
  CONTENTTYPE_FILE,
  CONTENTTYPE_REQUESTRUN,
  CONTENTTYPE_EMAIL
} from '../../constants'

const MUTATION_FILERELATION = gql`
  mutation createFileRelation($input: FileRelationMutationInput!) {
    filerelationMutate(input: $input) {
      filerelation {
        id
      }
    }
  }
`

export class RequestProcessingModal extends React.PureComponent {
  static propTypes = {
    objectId: PropTypes.string.isRequired,
    updateRequestRun: PropTypes.func.isRequired,
    initiateWorkflowRun: PropTypes.func,
    markFileAsTrash: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    chooseRequestRun: PropTypes.element
    // NOTE: see closed web_internal-file-request-procesing-fix-nav-for-file-turned-request branch for previously attempted nav functionality
  }

  static defaultProps = {
    chooseRequestRun: null
  }

  constructor(props) {
    super(props)

    this.state = {
      classifyOpen: false,
      allocateOpen: false,
      captureOpen: false,
      validateOpen: false,
      workflowOpen: false,
      showCreateCaseModal: false,
      assignedTo: null,
      displayEmail: null
    }

    this.requestPropertiesBarRef = React.createRef()
    this.requestProcessingModalRef = React.createRef()
    this.createCaseModalRef = React.createRef()
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (this.requestProcessingModalRef.current && !this.requestProcessingModalRef.current.contains(e.target) && !this.createCaseModalRef.current) {
      // click is outside, close drop-down(s)
      this.props.onClose()
    }

    if (this.createCaseModalRef.current && !this.createCaseModalRef.current.contains(e.target)) {
      // click is outside, close drop-down(s)
      this.setState({ showCreateCaseModal: false })
    }
  }

  showCheckIcon = (condition) => (
    condition ? 'check' : null
  )

  // NOTE: only setting to state value to "on_hold", "closed_cancelled" or "closed_duplicate" will be accepted as the rest of the statuses are set by backend
  updateRequestStatus = (status) => {
    this.props.updateRequestRun({
      id: this.props.objectId,
      status
    })
    .then(() => console.log('request run updated status successfully'))
    .catch(error => console.log('Failed to update request run status', error))
  }

  saveRequestAssignedTo = (assignedToIdentity) => {
    this.props.updateRequestRun({
      id: this.props.objectId,
      assignedTo: assignedToIdentity
    })
    .then(() => console.log('request run updated assignedTo successfully'))
    .catch(error => console.log('Failed to update request run assignedTo', error))
  }

  getCaseRelations = (file, requestRun) => {
    let relations = []
    if (requestRun) {
      if (requestRun.sources) {
        requestRun.sources.forEach(source => {
          relations.push({ objectId: decodeBase64(source.id), contentType: CONTENTTYPE_FILE })
          this.addEmailRelation(source, relations)
        })
      }
      relations.push({ objectId: decodeBase64(requestRun.id), contentType: CONTENTTYPE_REQUESTRUN })
    } else {
      relations.push({ objectId: decodeBase64(file.id), contentType: CONTENTTYPE_FILE })
      this.addEmailRelation(file, relations)
    }

    return relations
  }

  addEmailRelation = (file, relations) => {
    if (file.relations.edges.length > 0) {
      let emailRelation = file.relations.edges.find(relation => decodeBase64(relation.node.contentType.id) === CONTENTTYPE_EMAIL.toString())
      // if file has a filerelation to an email, add caserelation to email
      if (emailRelation) {
        relations.push({ objectId: emailRelation.node.objectId, contentType: CONTENTTYPE_EMAIL })
      }
    }
  }

  createFileRelationToCase = (filerelationMutate, fileId, caseId) => {
    let input = {
      file: fileId,
      contentType: CONTENTTYPE_CASE,
      objectId: decodeBase64(caseId)
    }
    filerelationMutate({ variables: { input } })
  }

  isUserContentCaptor = (requestRun) => (
    requestRun.capturedBy
      ? requestRun.capturedBy.id === localStorage.getItem('userUUID')
      : false
  )

  captureRequired = (requestRun) => (
    requestRun.content !== '{}'
  )

  updateRequestPropertiesBar = () => this.requestPropertiesBarRef.current.props.updateData()

  render() {
    let nodeType = getNodeTypeFromBase64Id(this.props.objectId)
    let isUnclassifiedFile = nodeType === 'FileNode'
    let query = isUnclassifiedFile
      ? QUERY_FILE
      : QUERY_REQUESTRUN

    return (
      <Query query={query} variables={{ id: this.props.objectId }} fetchPolicy='network-only'>
        {({ loading, error, data, refetch }) => {
          if (loading) {
            return <Spinner className='slds-is-relative slds-m-left_large' size='x-small' />
          }
          if (error) {
            return (
              // TODO: improve
              <Warning inline={true} className='slds-m-left_small' iconSize='x-small'>
                Failed to load options.
              </Warning>
            )
          }

          let requestRun = data.requestrun
          let file = data.file
          return (
            <Modal
              ref={this.requestProcessingModalRef}
              body={this.state.showCreateCaseModal
                ? <Mutation mutation={MUTATION_FILERELATION}>
                    {(filerelationMutate, { data }) => {
                      return (
                        <CreateCaseModal
                          forwardedRef={this.createCaseModalRef}
                          followUp={true}
                          affects={requestRun
                            ? requestRun.identity
                              ? requestRun.identity.id
                              : null
                            : null
                          }
                          caseRelations={this.getCaseRelations(file, requestRun)}
                          onCloseClick={() => this.setState({ showCreateCaseModal: false })}
                          onSaveClick={caseId => {
                            if (isUnclassifiedFile && caseId) {
                              this.createFileRelationToCase(filerelationMutate, file.id, caseId)
                              this.setState({ showCreateCaseModal: false })
                            } else {
                              this.setState({ showCreateCaseModal: false })
                            }
                          }}
                          onCaseRelationsCreated={this.updateRequestPropertiesBar}
                        />
                      )
                    }}
                  </Mutation>
                : <div className='request-processing-modal slds-size_1-of-1'>
                    <div className='request-processing-modal-nav slds-border_bottom page-navigation'>
                      <Back onClick={this.props.onClose} />
                    </div>
                    <div className='slds-scrollable_y request-processing-modal-file-view slds-size_1-of-1'>
                      {this.state.displayEmail
                        ? <DisplayEmail
                            email={this.state.displayEmail}
                            onClick={() => this.setState({ displayEmail: null })}
                          />
                        : isUnclassifiedFile
                          ? <FileView
                              file={file}
                              showMetaData={true}
                              displayEmail={email => this.setState({ displayEmail: email })}
                            />
                          : requestRun.sources.length > 0
                            ? <FileViewer
                                files={requestRun.sources}
                                inline={true}
                                preview={true}
                                showMetaData={true}
                                displayEmail={email => this.setState({ displayEmail: email })}
                              />
                            : <React.Fragment>
                                <div></div>
                                <CenteredContainerDisplay
                                  content={
                                    <div className='u-font-size-larger slds-text-color_inverse'>
                                      <SvgIcon
                                        variant='utility'
                                        symbol='info'
                                        colour='inverse'
                                        size='small'
                                        className='slds-m-right_small'
                                      />
                                      No files associated with this request.
                                    </div>
                                  }
                                  yAdjustPercentage={-20}
                                />
                              </React.Fragment>
                      }
                    </div>
                    <div className='slds-scrollable_y request-processing-modal-detail slds-size_1-of-1'>
                      {this.props.chooseRequestRunRender
                        ? this.props.chooseRequestRunRender
                        : <React.Fragment>
                            <Query query={QUERY_CASERELATIONS} variables={{ objectId: decodeBase64(this.props.objectId) }}>
                              {({ loading, error, data: crData, refetch }) => {
                                if (loading) {
                                  return <Spinner className='slds-is-relative slds-m-left_large' size='x-small' />
                                }
                                if (error) {
                                  return (
                                    // TODO: improve
                                    <Warning className='slds-m-left_small' iconSize='x-small'>
                                      Failed to load case information.
                                    </Warning>
                                  )
                                }

                                let openCases = crData.caserelations.edges.filter(caseRelation => (
                                  caseRelation.node.case.status === 'OPEN'
                                ))
                                let resolvedCases = crData.caserelations.edges.filter(caseRelation => (
                                  caseRelation.node.case.status === 'RESOLVED'
                                ))

                                return (
                                  <RequestPropertiesBar
                                    ref={this.requestPropertiesBarRef}
                                    objectId={this.props.objectId}
                                    allocatedIdentity={requestRun ? requestRun.identity : null}
                                    assignedToIdentity={requestRun ? requestRun.assignedTo : undefined}
                                    requestStatus={requestRun ? requestRun.status : 'unclassified'}
                                    openCases={openCases}
                                    resolvedCases={resolvedCases}
                                    onStatusUpdateClick={this.updateRequestStatus}
                                    onMarkFileAsTrashClick={this.props.markFileAsTrash}
                                    onCreateCaseClick={() => this.setState({ showCreateCaseModal: true })}
                                    assignedToSave={this.saveRequestAssignedTo}
                                    updateData={refetch}
                                  />
                                )
                              }}
                            </Query>
                            <div className='slds-m-around_large'>
                              <RequestClassify
                                objectId={this.props.objectId}  // can either be 'file' / 'requestrun'
                                requestRun={requestRun}
                                requestTypeId={requestRun ? requestRun.requesttype.id : null}
                                requestTypeName={requestRun ? requestRun.requesttype.name : null}
                                open={requestRun === undefined ? true : this.state.classifyOpen}
                                updateRequestRun={this.props.updateRequestRun}
                                onClick={() => this.setState(prevState => ({ classifyOpen: !prevState.classifyOpen }))}
                                onClassifySuccess={() => this.setState({
                                  classifyOpen: false,
                                  allocateOpen: true
                                })}
                              />
                              {requestRun &&
                                <React.Fragment>
                                  <RequestAllocate
                                    requestRun={requestRun}
                                    open={!requestRun.isAllocated || this.state.allocateOpen}
                                    updateRequestRun={this.props.updateRequestRun}
                                    saveNewPrimaryMsisdn={this.props.saveNewPrimaryMsisdn}
                                    locked={requestRun.requesttype === null ||
                                      (!requestRun.isAllocated && requestRun.status === 'ON_HOLD')
                                    }
                                    onClick={() => this.setState(prevState => ({ allocateOpen: !prevState.allocateOpen }))}
                                    onCreateCaseClick={() => this.setState({ showCreateCaseModal: true })}
                                    refreshData={refetch}
                                  />
                                  <RequestOrigin origin={requestRun.origin} />
                                  {this.captureRequired(requestRun) &&
                                    <RequestCapture
                                      requestRun={requestRun}
                                      requestType={requestRun.requesttype.id}
                                      open={this.state.captureOpen || (requestRun.identity && !requestRun.isCaptured)}
                                      updateRequestRun={this.props.updateRequestRun}
                                      locked={!requestRun.isAllocated}
                                      onClick={() => this.setState(prevState => ({ captureOpen: !prevState.captureOpen }))}
                                      saveProgress={() => {
                                        this.setState({ captureOpen: false })
                                        this.saveRequestAssignedTo('null')
                                      }}
                                    />
                                  }
                                  {requestRun.validationRequired &&
                                    <RequestValidate
                                      requestRun={requestRun}
                                      open={this.state.validateOpen}
                                      locked={requestRun.requesttype.authorisationRequired && requestRun.authorisedAt === null}
                                      updateRequestRun={this.props.updateRequestRun}
                                      userIsContentCaptor={this.isUserContentCaptor(requestRun)}
                                      toggleOpen={() => this.setState(prevState => ({ validateOpen: !prevState.validateOpen }))}
                                      onRejected={() => {
                                        if (requestRun.requesttype.workflow) {
                                          this.setState({ validateOpen: false, workflowOpen: true })
                                        } else {
                                          this.setState({ validateOpen: false })
                                        }
                                        this.saveRequestAssignedTo(requestRun.capturedBy.id)
                                      }}
                                      onValidated={() => this.setState({ validateOpen: false, workflowOpen: true })}
                                    />
                                  }
                                  {requestRun.requesttype.workflow &&
                                    <RequestWorkflow
                                      requestRun={requestRun}
                                      open={this.state.workflowOpen}
                                      captureRequired={this.captureRequired(requestRun)}
                                      initiateWorkflowRun={this.props.initiateWorkflowRun}
                                      onClick={() => this.setState(prevState => ({ workflowOpen: !prevState.workflowOpen }))}
                                    />
                                  }
                                </React.Fragment>
                              }
                            </div>
                          </React.Fragment>
                      }
                    </div>
                  </div>
              }
              sizeXLarge={true}
              contentPadding='none'
            />
          )
        }}
      </Query>
    )
  }
}

const DisplayEmail = ({ email, onClick }) => (
  <React.Fragment>
    <div className='slds-m-around_medium' style={{ position: 'relative' }}>
      <div className='align-inside-div-bottom-right'>
        <Button onClick={onClick}>
          Close email
        </Button>
      </div>
    </div>
    <Email id={email} className='slds-p-around_medium slds-m-around_medium' />
  </React.Fragment>
)



// WEBPACK FOOTER //
// ./src/components/request-processing-modal/index.js